import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { ShieldCheck, AlertTriangle, RefreshCw, Banknote, Users, Receipt, Database, Sparkles, Wand2, Eye } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { cur } from '@/lib/currency';

const Stat = ({ label, value, tone = 'default', testid, sub }) => {
  const toneCls = {
    default: 'text-foreground',
    good: 'text-emerald-600 dark:text-emerald-300',
    bad: 'text-rose-600 dark:text-rose-300',
    warn: 'text-amber-600 dark:text-amber-300',
  }[tone] || 'text-foreground';
  return (
    <div className="p-3 rounded-lg border border-border bg-background/50" data-testid={testid}>
      <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`text-2xl font-bold tabular-nums mt-1 ${toneCls}`}>{value}</div>
      {sub ? <div className="text-[10px] text-muted-foreground mt-0.5">{sub}</div> : null}
    </div>
  );
};

// iter503 — FixCard: a dangling-stat that's also clickable to open
// the Auto-Fix wizard for that category. When onFix is null we render
// a "Manual review required" hint instead of a button.
const FixCard = ({ label, count, onFix, tone, note, linkTo, linkLabel, testid, onDetails }) => {
  const bad = (count || 0) > 0;
  const toneCls = !bad ? 'text-emerald-600 dark:text-emerald-300'
    : tone === 'warn' ? 'text-amber-600 dark:text-amber-300'
    : 'text-rose-600 dark:text-rose-300';
  return (
    <div className="p-3 rounded-lg border border-border bg-background/50 flex items-center gap-3" data-testid={testid}>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground truncate">{label}</div>
        <div className={`text-2xl font-bold tabular-nums mt-1 ${toneCls}`}>{count}</div>
      </div>
      <div className="flex flex-col gap-1 shrink-0">
        {bad && onDetails ? (
          <Button size="sm" variant="ghost" className="h-6 text-[10px] px-2 text-blue-600 hover:bg-blue-50" onClick={onDetails} data-testid={`${testid}-details`}>
            <Eye size={11} className="mr-1" /> Details
          </Button>
        ) : null}
        {bad && onFix ? (
          <Button size="sm" variant="outline" className="h-7 text-[10px] border-emerald-300 text-emerald-700 hover:bg-emerald-50" onClick={onFix} data-testid={`${testid}-fix`}>
            <Wand2 size={11} className="mr-1" /> Fix
          </Button>
        ) : bad && linkTo ? (
          <Link to={linkTo} className="shrink-0">
            <Button size="sm" variant="outline" className="h-7 text-[10px] border-blue-300 text-blue-700 hover:bg-blue-50" data-testid={`${testid}-review`}>
              {linkLabel || 'Review'}
            </Button>
          </Link>
        ) : note && !bad ? (
          <span className="text-[9px] text-muted-foreground italic shrink-0">{note}</span>
        ) : null}
      </div>
    </div>
  );
};

const SectionCard = ({ icon: Icon, title, children, testid }) => (
  <Card data-testid={testid}>
    <CardContent className="p-4">
      <div className="flex items-center gap-2 mb-3">
        <Icon size={16} className="text-muted-foreground" />
        <h3 className="text-sm font-bold">{title}</h3>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
        {children}
      </div>
    </CardContent>
  </Card>
);

/**
 * App Audit (iter502) — Read-only diagnostic dashboard for tenant data
 * hygiene. Shows counts, dangling references, and a derived health
 * score so admins can spot ledger problems before they hit reports.
 */
export default function AppAuditPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [branches, setBranches] = useState([]);
  // iter503 — Auto-Fix wizard state. `wizardKind` selects which
  // dangling category we're fixing, `wizardBranch` is the default
  // branch when relevant, `wizardPreview` holds the dry-run count.
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardKind, setWizardKind] = useState(null);
  const [wizardBranch, setWizardBranch] = useState('');
  const [wizardPreview, setWizardPreview] = useState(null);
  const [wizardSubmitting, setWizardSubmitting] = useState(false);
  // iter942 — read-only "View Details" dialog for a dangling category
  const [detailKind, setDetailKind] = useState(null);
  const [detailTitle, setDetailTitle] = useState('');
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const openDetails = async (kind, title) => {
    setDetailKind(kind); setDetailTitle(title); setDetailData(null); setDetailLoading(true);
    try {
      const r = await api.get(`/app-audit/details/${kind}`);
      setDetailData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load details');
    } finally { setDetailLoading(false); }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [r, b] = await Promise.all([
        api.get('/app-audit/summary'),
        api.get('/branches').catch(() => ({ data: [] })),
      ]);
      setData(r.data);
      setBranches(Array.isArray(b.data) ? b.data : (b.data?.branches || []));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load audit');
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const WIZARD_KINDS = {
    'expenses-no-branch': {
      label: 'Expenses missing a branch',
      endpoint: '/app-audit/fix/expenses-no-branch',
      needsBranch: true,
      description: 'Assign every expense without a branch to the picked default branch.',
    },
    'salaries-no-branch': {
      label: 'Salaries missing a branch',
      endpoint: '/app-audit/fix/salaries-no-branch',
      needsBranch: true,
      description: 'Inherit the employee\'s home branch when possible; fall back to the picked default branch otherwise.',
    },
    'detach-dangling-funding': {
      label: 'Dangling funding-advance refs',
      endpoint: '/app-audit/fix/detach-dangling-funding',
      needsBranch: false,
      description: 'Detach the `funded_by_advance_id` field from every expense whose advance was deleted. The expense itself is kept intact.',
    },
    'post-fines': {
      label: 'Unposted fines',
      endpoint: '/app-audit/fix/post-fines',
      needsBranch: false,
      description: 'For every fine without an expense_id, create a matching expense row and back-link it.',
    },
  };

  const openWizard = async (kind) => {
    setWizardKind(kind);
    setWizardBranch('');
    setWizardPreview(null);
    setWizardOpen(true);
    // Dry-run preview — for kinds that need a branch we wait until
    // the user picks one (preview re-fires on branch change).
    const cfg = WIZARD_KINDS[kind];
    if (!cfg.needsBranch) {
      try {
        const r = await api.post(cfg.endpoint, { dry_run: true });
        setWizardPreview(r.data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Preview failed');
      }
    }
  };

  const refreshWizardPreview = async () => {
    if (!wizardKind) return;
    const cfg = WIZARD_KINDS[wizardKind];
    if (!cfg.needsBranch) return;
    if (!wizardBranch) { setWizardPreview(null); return; }
    try {
      const r = await api.post(cfg.endpoint, { default_branch_id: wizardBranch, dry_run: true });
      setWizardPreview(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Preview failed');
    }
  };

  useEffect(() => { refreshWizardPreview(); /* eslint-disable-next-line */ }, [wizardBranch, wizardKind]);

  const submitWizard = async () => {
    if (!wizardKind) return;
    const cfg = WIZARD_KINDS[wizardKind];
    if (cfg.needsBranch && !wizardBranch) {
      toast.error('Pick a default branch'); return;
    }
    setWizardSubmitting(true);
    try {
      const payload = cfg.needsBranch ? { default_branch_id: wizardBranch } : {};
      const r = await api.post(cfg.endpoint, payload);
      toast.success(`Auto-fix complete · ${r.data?.fixed || 0} row${r.data?.fixed === 1 ? '' : 's'} fixed`);
      setWizardOpen(false);
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Auto-fix failed');
    } finally { setWizardSubmitting(false); }
  };

  const health = data?.health_score ?? 0;
  const healthTone = health >= 80 ? 'good' : health >= 50 ? 'warn' : 'bad';
  const banking = data?.banking || {};
  const dangling = data?.dangling || {};
  const linkedPct = banking.transactions_total
    ? Math.round((banking.transactions_linked / banking.transactions_total) * 100)
    : 0;

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-[1400px] mx-auto" data-testid="app-audit-page">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg shadow-emerald-200/50">
              <ShieldCheck size={22} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground" data-testid="audit-title">App Audit</h1>
              <p className="text-sm text-muted-foreground mt-0.5">Self-service diagnostic — spot data-hygiene problems before they hit your reports.</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={fetchData} disabled={loading} data-testid="audit-refresh">
            <RefreshCw size={14} className={`mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>

        {loading && !data ? (
          <Card><CardContent className="p-6 text-center text-sm text-muted-foreground">Running audit…</CardContent></Card>
        ) : !data ? (
          <Card><CardContent className="p-6 text-center text-sm text-muted-foreground">No audit data.</CardContent></Card>
        ) : (
          <>
            {/* Health hero */}
            <Card data-testid="audit-hero">
              <CardContent className="p-6 flex items-center gap-6 flex-wrap">
                <div className="flex items-center gap-4">
                  <div className={`w-20 h-20 rounded-full flex items-center justify-center text-3xl font-bold tabular-nums ${
                    healthTone === 'good' ? 'bg-emerald-100 text-emerald-700' :
                    healthTone === 'warn' ? 'bg-amber-100 text-amber-700' :
                    'bg-rose-100 text-rose-700'
                  }`} data-testid="audit-health-score">{health}</div>
                  <div>
                    <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Health Score</div>
                    <div className="text-lg font-bold">
                      {healthTone === 'good' ? 'All clean' : healthTone === 'warn' ? 'A few things to fix' : 'Needs attention'}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      100 minus 1 per dangling reference. Audit run at {data.generated_at?.slice(0, 19).replace('T', ' ')} UTC.
                    </div>
                  </div>
                </div>
                <div className="ml-auto flex items-center gap-4 flex-wrap">
                  <div className="text-right">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Bank Linked</div>
                    <div className="text-2xl font-bold tabular-nums">{linkedPct}%</div>
                    <div className="text-[10px] text-muted-foreground">{banking.transactions_linked} / {banking.transactions_total} txns</div>
                  </div>
                  {banking.oldest_unlinked_age_days != null && (
                    <Badge variant="outline" className={banking.oldest_unlinked_age_days > 60 ? 'border-amber-400 text-amber-700' : ''} data-testid="audit-oldest-unlinked-badge">
                      <AlertTriangle size={11} className="mr-1" />
                      Oldest unlinked: {banking.oldest_unlinked_age_days}d
                    </Badge>
                  )}
                </div>
              </CardContent>
            </Card>

            <SectionCard icon={Users} title="Core Entities" testid="audit-core-section">
              <Stat label="Employees" value={data.core_counts?.employees ?? 0} testid="audit-emp-count" />
              <Stat label="Partners" value={data.core_counts?.partners ?? 0} testid="audit-partner-count" />
              <Stat label="Suppliers" value={data.core_counts?.suppliers ?? 0} testid="audit-supplier-count" />
              <Stat label="Customers" value={data.core_counts?.customers ?? 0} testid="audit-customer-count" />
              <Stat label="Branches" value={data.core_counts?.branches ?? 0} testid="audit-branch-count" />
            </SectionCard>

            <SectionCard icon={Banknote} title="Banking" testid="audit-banking-section">
              <Stat label="Statements" value={banking.statements ?? 0} testid="audit-stmt-count" />
              <Stat label="Transactions" value={banking.transactions_total ?? 0} testid="audit-txn-count" />
              <Stat label="Linked" value={banking.transactions_linked ?? 0} tone="good" testid="audit-linked-count" />
              <Stat label="Un-linked Debits" value={banking.transactions_unlinked ?? 0} tone={banking.transactions_unlinked > 0 ? 'warn' : 'good'} testid="audit-unlinked-count" />
              <Stat label="Oldest Unlinked" value={banking.oldest_unlinked_age_days != null ? `${banking.oldest_unlinked_age_days}d` : '—'} tone={(banking.oldest_unlinked_age_days || 0) > 60 ? 'bad' : 'default'} testid="audit-oldest-stat" />
            </SectionCard>

            <Card data-testid="audit-dangling-section">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <AlertTriangle size={16} className="text-muted-foreground" />
                  <h3 className="text-sm font-bold">Dangling References (data hygiene)</h3>
                  <Badge variant="outline" className="text-[10px] ml-2">click Fix to auto-clean</Badge>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  <FixCard
                    label="Expenses · No Branch"
                    count={dangling.expenses_no_branch ?? 0}
                    onFix={() => openWizard('expenses-no-branch')}
                    onDetails={() => openDetails('expenses-no-branch', 'Expenses · No Branch')}
                    testid="audit-exp-no-branch"
                  />
                  <FixCard
                    label="Funding Advance Missing"
                    count={dangling.expenses_dangling_funding_advance ?? 0}
                    onFix={() => openWizard('detach-dangling-funding')}
                    onDetails={() => openDetails('funding-advance-missing', 'Expenses · Dangling Funding Advance')}
                    testid="audit-funding-missing"
                  />
                  <FixCard
                    label="Fines Not Posted"
                    count={dangling.fines_unlinked_to_expense ?? 0}
                    onFix={() => openWizard('post-fines')}
                    onDetails={() => openDetails('fines-unlinked', 'Fines · Not Posted')}
                    tone="warn"
                    testid="audit-fines-unlinked"
                  />
                  <FixCard
                    label="SP · Missing Supplier"
                    count={dangling.supplier_payments_missing_supplier ?? 0}
                    onFix={null}
                    onDetails={() => openDetails('supplier-payments-missing', 'Supplier Payments · Missing Supplier')}
                    linkTo="/app-audit/orphans/supplier-payments"
                    linkLabel="Review"
                    testid="audit-sp-missing"
                  />
                  <FixCard
                    label="Salaries · No Branch"
                    count={dangling.salary_payments_no_branch ?? 0}
                    onFix={() => openWizard('salaries-no-branch')}
                    onDetails={() => openDetails('salaries-no-branch', 'Salaries · No Branch')}
                    tone="warn"
                    testid="audit-sal-no-branch"
                  />
                </div>
              </CardContent>
            </Card>

            {(data.mapping_guard?.count ?? 0) > 0 && (
              <Card data-testid="audit-mapping-guard" className="border-amber-300 dark:border-amber-700">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle size={16} className="text-amber-600 mt-0.5 shrink-0" />
                    <div className="text-xs leading-relaxed">
                      <strong className="text-amber-700 dark:text-amber-400">Mapping Guard — {data.mapping_guard.count} Supplier/Purchase categor{data.mapping_guard.count === 1 ? 'y is' : 'ies are'} not mapped to an account.</strong>
                      <p className="text-muted-foreground mt-1">
                        Without an explicit account mapping, these COGS-driving categories can be misclassified and cause your
                        Cost of Goods and Accounts Payable to drift apart. Map them in Chart of Accounts → Category Mappings.
                      </p>
                      <div className="flex flex-wrap gap-1.5 mt-2 items-center">
                        {data.mapping_guard.unmapped_cogs_categories.map(c => (
                          <Link key={c} to={`/category-account-mapping?category=${encodeURIComponent(c)}`} className="px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 font-medium hover:ring-1 hover:ring-amber-400" data-testid={`unmapped-cat-${c}`}>{c}</Link>
                        ))}
                        <Link to={`/category-account-mapping?category=${encodeURIComponent(data.mapping_guard.unmapped_cogs_categories[0] || '')}`} className="ml-1 px-3 py-1 rounded-lg bg-amber-600 text-white text-[11px] font-semibold hover:bg-amber-700" data-testid="mapping-guard-map-now">Map Now →</Link>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {data.parity_guard && (
              <Card data-testid="audit-parity-guard" className={data.parity_guard.in_sync ? 'border-emerald-300 dark:border-emerald-700' : 'border-rose-300 dark:border-rose-700'}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle size={16} className={`mt-0.5 shrink-0 ${data.parity_guard.in_sync ? 'text-emerald-600' : 'text-rose-600'}`} />
                    <div className="text-xs leading-relaxed w-full">
                      {data.parity_guard.in_sync ? (
                        <>
                          <strong className="text-emerald-700 dark:text-emerald-400" data-testid="parity-guard-ok">Parity Guard — all {data.parity_guard.checked} branches in sync ✓</strong>
                          <p className="text-muted-foreground mt-1">
                            Every branch's Net Profit matches between the Branch Summary and the Multi-Branch P&L Matrix. The Global Math Lock is holding.
                          </p>
                        </>
                      ) : (
                        <>
                          <strong className="text-rose-700 dark:text-rose-400" data-testid="parity-guard-drift">Parity Guard — {data.parity_guard.drifts.length} branch{data.parity_guard.drifts.length === 1 ? '' : 'es'} drifted between Summary and Matrix.</strong>
                          <p className="text-muted-foreground mt-1">
                            These branches show a different Net Profit on the Branch Summary vs the P&L Matrix. Open both reports to investigate.
                          </p>
                          <div className="mt-2 space-y-1">
                            {data.parity_guard.drifts.map(d => (
                              <div key={d.branch_id} className="flex items-center justify-between gap-2 px-2 py-1 rounded bg-rose-50 dark:bg-rose-950/30" data-testid={`parity-drift-${d.branch_id}`}>
                                <span className="font-medium text-rose-800 dark:text-rose-300">{d.branch_name}</span>
                                <span className="tabular-nums text-muted-foreground">Summary {d.summary_net.toLocaleString()} · Matrix {d.matrix_net.toLocaleString()} · Δ {d.diff.toLocaleString()}</span>
                              </div>
                            ))}
                          </div>
                          <div className="flex gap-2 mt-2">
                            <Link to="/branch-summary" className="px-3 py-1 rounded-lg bg-rose-600 text-white text-[11px] font-semibold hover:bg-rose-700" data-testid="parity-open-summary">Open Branch Summary →</Link>
                            <Link to="/multi-branch-pnl-matrix" className="px-3 py-1 rounded-lg bg-secondary text-foreground text-[11px] font-semibold hover:bg-secondary/70" data-testid="parity-open-matrix">Open P&L Matrix →</Link>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            <SectionCard icon={Sparkles} title="Automation Library" testid="audit-presets-section">
              <Stat label="Bulk-Convert Presets" value={data.presets?.bulk_convert_presets_count ?? 0} testid="audit-presets-count" />
              <Stat label="Smart-Group Rules" value={data.presets?.smart_group_rules_count ?? 0} testid="audit-rules-count" />
            </SectionCard>

            <SectionCard icon={Receipt} title="Salary" testid="audit-salary-section">
              <Stat label="Total Salary Payments" value={data.salary?.salary_payments_count ?? 0} testid="audit-salary-total" />
              <Stat label="Salaries Missing Branch" value={data.salary?.salary_payments_no_branch ?? 0} tone={(data.salary?.salary_payments_no_branch || 0) > 0 ? 'warn' : 'good'} testid="audit-salary-no-branch" />
            </SectionCard>

            <Card data-testid="audit-action-card">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <Database size={16} className="text-muted-foreground mt-0.5 shrink-0" />
                  <div className="text-xs text-muted-foreground leading-relaxed">
                    <strong>What to do next?</strong> Open each affected page (Expenses, Salary, Suppliers, Pass-Through Advances)
                    and use its filters to find rows that match these counts. Most dangling references
                    can be fixed by editing the row and re-saving with the correct branch / supplier / advance.
                    Re-run this audit to see your score climb.
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {/* iter503 — Auto-Fix wizard dialog */}
        <Dialog open={wizardOpen} onOpenChange={(o) => { setWizardOpen(o); if (!o) { setWizardKind(null); setWizardPreview(null); } }}>
          <DialogContent className="sm:max-w-md" data-testid="audit-wizard-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Wand2 size={16} className="text-emerald-600" /> Auto-Fix Wizard
              </DialogTitle>
              {wizardKind && WIZARD_KINDS[wizardKind] && (
                <DialogDescription>
                  <strong>{WIZARD_KINDS[wizardKind].label}</strong> · {WIZARD_KINDS[wizardKind].description}
                </DialogDescription>
              )}
            </DialogHeader>
            <div className="space-y-3">
              {wizardKind && WIZARD_KINDS[wizardKind]?.needsBranch && (
                <div>
                  <Label>Default branch *</Label>
                  <Select value={wizardBranch} onValueChange={setWizardBranch}>
                    <SelectTrigger data-testid="wizard-branch-select"><SelectValue placeholder="Pick a branch" /></SelectTrigger>
                    <SelectContent>
                      {branches.map(b => (
                        <SelectItem key={b.id} value={b.id} data-testid={`wizard-branch-${b.id}`}>{b.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {wizardPreview && (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-3 text-sm" data-testid="wizard-preview-banner">
                  Would fix <strong className="text-emerald-700" data-testid="wizard-would-fix-count">{wizardPreview.would_fix ?? 0}</strong> row{(wizardPreview.would_fix ?? 0) === 1 ? '' : 's'}.
                  {wizardPreview.branch_name ? <> Target branch: <strong>{wizardPreview.branch_name}</strong>.</> : null}
                </div>
              )}
              {!wizardPreview && wizardKind && WIZARD_KINDS[wizardKind].needsBranch && !wizardBranch && (
                <p className="text-xs text-muted-foreground italic">Pick a branch to see how many rows would be affected.</p>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setWizardOpen(false)} disabled={wizardSubmitting}>Cancel</Button>
              <Button
                onClick={submitWizard}
                disabled={wizardSubmitting || !wizardPreview || (wizardPreview.would_fix ?? 0) === 0}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="wizard-submit-btn"
              >
                {wizardSubmitting ? 'Fixing…' : `Fix ${wizardPreview?.would_fix ?? 0} row${(wizardPreview?.would_fix ?? 0) === 1 ? '' : 's'}`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* iter942 — Read-only "View Details" dialog for a dangling category */}
        <Dialog open={!!detailKind} onOpenChange={(o) => { if (!o) { setDetailKind(null); setDetailData(null); } }}>
          <DialogContent className="max-w-3xl" data-testid="audit-details-dialog">
            <DialogHeader>
              <DialogTitle>{detailTitle}</DialogTitle>
              <DialogDescription>
                {detailLoading ? 'Loading…' : detailData
                  ? `${detailData.count} record(s) · Total ${cur(detailData.total_amount)}`
                  : ''}
              </DialogDescription>
            </DialogHeader>
            <div className="max-h-[60vh] overflow-auto">
              {detailData?.records?.length ? (
                <table className="w-full text-xs" data-testid="audit-details-table">
                  <thead className="sticky top-0 bg-background border-b">
                    <tr className="text-left text-muted-foreground">
                      <th className="p-2 font-medium">Date</th>
                      <th className="p-2 font-medium text-right">Amount</th>
                      <th className="p-2 font-medium">Category</th>
                      <th className="p-2 font-medium">Entered By</th>
                      <th className="p-2 font-medium">Description / Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detailData.records.map((r, i) => (
                      <tr key={r.id || i} className="border-b border-border/40" data-testid={`audit-detail-row-${i}`}>
                        <td className="p-2 whitespace-nowrap">{r.date || '—'}</td>
                        <td className="p-2 text-right font-mono">{cur(r.amount)}</td>
                        <td className="p-2">{r.category}</td>
                        <td className="p-2">{r.entered_by}</td>
                        <td className="p-2 text-muted-foreground">{r.description}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : !detailLoading ? (
                <p className="text-sm text-muted-foreground text-center py-8">No records found.</p>
              ) : null}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setDetailKind(null); setDetailData(null); }} data-testid="audit-details-close">Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
